import { createStore, produce } from "solid-js/store";
import { useWebSocket } from "~/modules/shared/utils/useWebSocket";

export type QuestionStep = {
	title: string;
	/** 0 to 1; null while the step has no measurable progress. */
	percentage: number | null;
};

type StatusUpdate = {
	id: string;
	status: string;
	progress?: QuestionStep | null;
};

/**
 * Questions that are running now, by public id. An entry exists exactly while
 * the question runs, so a missing id means "not running or not known".
 * Module-level so every list on a page reads the same state from one feed.
 */
const [running, setRunning] = createStore<Record<string, QuestionStep>>({});

const NO_STEP: QuestionStep = { title: "", percentage: null };

/**
 * Distinct steps each running question has reported. The pipeline does not
 * know its steps in advance, so this is all the bar has to go on.
 */
const [stepCounts, setStepCounts] = createStore<Record<string, number>>({});

/** Questions that completed a moment ago, so their bar can show 100% before going. */
const [justCompleted, setJustCompleted] = createStore<Record<string, true>>({});

const COMPLETED_LINGER_MS = 1200;
const lingerTimers = new Map<string, ReturnType<typeof setTimeout>>();

function endLinger(id: string) {
	clearTimeout(lingerTimers.get(id));
	lingerTimers.delete(id);
	setJustCompleted(
		produce((state) => {
			delete state[id];
		}),
	);
}

/**
 * Steps at which the estimate reaches about 63% of its ceiling. High-effort
 * runs report up to ~25 step changes (about 15 typically), medium ones about
 * 4 to 12, so this keeps a long run moving instead of pinned at the ceiling.
 */
const TYPICAL_STEP_COUNT = 8;
/** Left unreached while running, so a bar at the ceiling never reads as done. */
const RUNNING_CEILING = 0.9;

/**
 * Questions already seeded or pushed. Page data is a snapshot from page load,
 * and a sidebar that remounts re-reads it: seeding twice would bring back the
 * spinner of a question whose finish push already cleared it.
 */
const seen = new Set<string>();

/** Rows from the server carry `progress` only while their question is running. */
export function seedQuestionActivity(
	rows: ReadonlyArray<{ id: string; progress?: QuestionStep | null }>,
) {
	for (const row of rows) {
		if (seen.has(row.id)) {
			continue;
		}
		seen.add(row.id);
		if (row.progress) {
			setRunning(row.id, row.progress);
			setStepCounts(row.id, 1);
		}
	}
}

/** Ids of the questions running now. Tracks only which ids are present, not their steps. */
export const runningQuestionIds = (): string[] => Object.keys(running);

/** The step a question is on, or undefined when it is not running. */
export const questionActivity = (id: string): QuestionStep | undefined =>
	running[id];

/**
 * How far along a running question is, 0 to 1. Faked from the number of steps
 * reported so far: it only ever moves forward, slows down as steps pile up, and
 * stays under 100% until the question completes. Undefined when not running.
 */
export const questionProgress = (id: string): number | undefined => {
	if (justCompleted[id]) {
		return 1;
	}
	if (!running[id]) {
		return undefined;
	}
	const steps = stepCounts[id] ?? 1;
	return RUNNING_CEILING * (1 - Math.exp(-steps / TYPICAL_STEP_COUNT));
};

/** True for a moment after a question completes, while its bar shows 100%. */
export const questionJustCompleted = (id: string): boolean =>
	justCompleted[id] === true;

function forget(id: string) {
	setRunning(
		produce((state) => {
			delete state[id];
		}),
	);
	setStepCounts(
		produce((state) => {
			delete state[id];
		}),
	);
}

export function applyQuestionStatus(update: StatusUpdate) {
	seen.add(update.id);
	if (update.status === "running") {
		endLinger(update.id);
		const previous = running[update.id];
		const next = update.progress ?? previous ?? NO_STEP;
		const isNewStep =
			next.title.trim() !== "" && next.title !== previous?.title;
		setRunning(update.id, next);
		if (isNewStep || stepCounts[update.id] === undefined) {
			setStepCounts(update.id, (stepCounts[update.id] ?? 0) + 1);
		}
		return;
	}
	const wasRunning = running[update.id] !== undefined;
	forget(update.id);
	if (wasRunning && update.status === "completed") {
		endLinger(update.id);
		setJustCompleted(update.id, true);
		lingerTimers.set(
			update.id,
			setTimeout(() => endLinger(update.id), COMPLETED_LINGER_MS),
		);
	}
}

/**
 * Keeps the store live from the asker's own channel, which also carries
 * consumer and agency questions that no influencer channel hears. Call from
 * any list: the channel is shared, so the cost is one subscription however many
 * lists read the store. Pushes missed while offline are not replayed; a page reload
 * reseeds from the server.
 */
export function useQuestionActivityFeed() {
	const userId = window.pageVars?.activityUserId;
	useWebSocket<StatusUpdate>({
		channel: userId ? `users.${userId}` : undefined,
		event: ".question.status-changed",
		onMessage: applyQuestionStatus,
	});
}
